import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ShieldCheck, Lock, KeyRound, ArrowRight, Eye, EyeOff, ArrowLeft, AlertTriangle } from "lucide-react";

import { setToken } from "../../utils/tokenStorage";

export default function MasterLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const nav = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch("/api/master/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      setToken("master_token", data.token);
      toast.success("Welcome, Master Admin");
      nav("/mascop-2k26/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="au-root" style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: 20, position: "relative",
    }}>
      <div className="au-glass au-pad-lg" style={{
        maxWidth: 420, width: "100%", position: "relative", zIndex: 5,
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 44, height: 44, margin: "0 auto 16px",
            borderRadius: 12, display: "grid", placeItems: "center",
            background: "var(--au-accent)", color: "var(--au-btn-text)",
          }}>
            <ShieldCheck size={22} />
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Master Console</h2>
          <p style={{
            color: "var(--au-text-2)", fontSize: 13,
            margin: "8px 0 0", lineHeight: 1.5,
          }}>AEGIS owner control panel</p>
        </div>

        {!showForgot ? (
          <form onSubmit={login}>
            <Label>Username</Label>
            <input
              className="au-input"
              type="text" required autoComplete="username"
              value={username} onChange={(e) => setUsername(e.target.value)}
              placeholder="Master username"
              style={{ marginBottom: 16 }}
            />

            <Label>Password</Label>
            <div style={{ position: "relative", marginBottom: 16 }}>
              <input
                className="au-input"
                type={showPwd ? "text" : "password"} required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Master password"
                style={{ paddingRight: 44 }}
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} tabIndex={-1}
                aria-label={showPwd ? "Hide password" : "Show password"}
                style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  background: "transparent", border: "none", color: "var(--au-muted)",
                  cursor: "pointer", padding: 6, lineHeight: 0, display: "grid", placeItems: "center",
                }}>
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button type="submit" disabled={busy} className="au-pill au-pill-primary" style={{
              width: "100%", marginTop: 8,
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.75 : 1,
            }}>
              <Lock size={18} />
              {busy ? "Authenticating…" : "Authenticate"}
            </button>

            <button type="button" onClick={() => setShowForgot(true)} className="au-pill au-pill-ghost" style={{
              width: "100%", marginTop: 12,
            }}>Forgot password?</button>
          </form>
        ) : (
          <ForgotPassword goBack={() => setShowForgot(false)} />
        )}

        <div style={{
          marginTop: 24, padding: 14,
          display: "flex", alignItems: "center", gap: 10,
          background: "rgba(242,80,60,0.08)",
          border: "1px solid rgba(242,80,60,0.25)",
          borderRadius: 12,
          fontSize: 12, color: "var(--au-text-2)", lineHeight: 1.5,
        }}>
          <AlertTriangle size={16} style={{ color: "var(--au-error)", flex: "none" }} />
          <span>Unauthorized access attempts are logged and trigger alerts.</span>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (<label style={{
    display: "block", fontSize: 11, color: "var(--au-text-2)",
    marginBottom: 6, letterSpacing: 1,
    textTransform: "uppercase", fontWeight: 600,
  }}>{children}</label>);
}

function ForgotPassword({ goBack }) {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [requestId, setRequestId] = useState("");
  const [otp1, setOtp1] = useState("");
  const [otp2, setOtp2] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [hints, setHints] = useState({ primary: "", backup: "" });
  const [busy, setBusy] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  const initReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch("/api/master/forgot-init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Init failed");
      setRequestId(data.request_id);
      setHints({ primary: data.primary_hint, backup: data.backup_hint });
      setStep(2);
      toast.success("OTPs sent to both emails");
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const verifyAndReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 12) { toast.error("Password must be 12+ characters"); return; }
    setBusy(true);
    try {
      const res = await fetch("/api/master/forgot-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: requestId, otp1, otp2, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      toast.success("Password reset. Login with new password.");
      goBack();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  if (step === 1) {
    return (
      <form onSubmit={initReset}>
        <h3 style={{ fontSize: 16, margin: "0 0 12px" }}>Reset Master Password</h3>
        <p style={{ color: "var(--au-text-2)", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
          OTPs will be sent to <strong>both</strong> registered emails.
        </p>
        <Label>Master Username</Label>
        <input
          className="au-input" required value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter master username"
          style={{ marginBottom: 16 }}
        />
        <button type="submit" disabled={busy} className="au-pill au-pill-primary" style={{
          width: "100%", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.75 : 1,
        }}>
          {busy ? "Sending OTPs…" : "Send Dual OTPs"}
          {!busy && <ArrowRight size={18} />}
        </button>
        <button type="button" onClick={goBack} className="au-pill au-pill-ghost" style={{
          width: "100%", marginTop: 12,
        }}>
          <ArrowLeft size={18} />
          Back to login
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyAndReset}>
      <h3 style={{ fontSize: 16, margin: "0 0 12px" }}>Enter Both OTPs</h3>
      <p style={{ color: "var(--au-text-2)", fontSize: 13, marginBottom: 18, lineHeight: 1.7 }}>
        OTP 1 → <strong style={{ color: "var(--au-info)" }}>{hints.primary}</strong><br />
        OTP 2 → <strong style={{ color: "var(--au-success)" }}>{hints.backup}</strong>
      </p>

      <Label>OTP 1 (Primary Email)</Label>
      <input
        className="au-input au-mono" required maxLength={6} value={otp1}
        onChange={(e) => setOtp1(e.target.value.replace(/\D/g, ""))}
        placeholder="000000"
        style={{ letterSpacing: 8, textAlign: "center", fontSize: 20, marginBottom: 16 }}
      />

      <Label>OTP 2 (Backup Email)</Label>
      <input
        className="au-input au-mono" required maxLength={6} value={otp2}
        onChange={(e) => setOtp2(e.target.value.replace(/\D/g, ""))}
        placeholder="000000"
        style={{ letterSpacing: 8, textAlign: "center", fontSize: 20, marginBottom: 16 }}
      />

      <Label>New Password (12+ chars)</Label>
      <div style={{ position: "relative", marginBottom: 16 }}>
        <input
          className="au-input"
          type={showNewPwd ? "text" : "password"} required minLength={12}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Strong new password"
          style={{ paddingRight: 44 }}
        />
        <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} tabIndex={-1}
          aria-label={showNewPwd ? "Hide password" : "Show password"}
          style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "transparent", border: "none", color: "var(--au-muted)",
            cursor: "pointer", padding: 6, lineHeight: 0, display: "grid", placeItems: "center",
          }}>
          {showNewPwd ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      <button type="submit" disabled={busy} className="au-pill au-pill-primary" style={{
        width: "100%", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.75 : 1,
      }}>
        <KeyRound size={18} />
        {busy ? "Resetting…" : "Verify & Reset"}
      </button>
      <button type="button" onClick={goBack} className="au-pill au-pill-ghost" style={{
        width: "100%", marginTop: 12,
      }}>
        <ArrowLeft size={18} />
        Cancel
      </button>
    </form>
  );
}
